import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import Login from './Login';
import ClientConfirmation from './pages/ClientConfirmation';
import DutyTracker from './pages/DutyTracker';
import AdminLayout from './admin/AdminLayout';
import Dashboard from './admin/Dashboard';
import CRM from './admin/CRM';
import HR from './admin/HR';
import Clients from './admin/Clients';
import Billing from './admin/Billing';
import AccessControl from './admin/AccessControl';

// 99 Care Public Pages
import Layout from './components/layout/Layout';
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import ServicesPage from './pages/public/ServicesPage';
import ServiceDetailPage from './pages/public/ServiceDetailPage';
import BlogPage from './pages/public/BlogPage';
import BlogDetailPage from './pages/public/BlogDetailPage';
import ContactPage from './pages/public/ContactPage';
import AppointmentPage from './pages/public/AppointmentPage';
import AppointmentConfirmedPage from './pages/public/AppointmentConfirmedPage';
import NotFoundPage from './pages/NotFoundPage';
import PublicIDCard from './pages/public/PublicIDCard';
import ReviewsPage from './pages/public/ReviewsPage';
import PrivacyPolicyPage from './pages/public/PrivacyPolicyPage';
import TermsPage from './pages/public/TermsPage';
import { APP_MODE, getAppMode } from './config/appMode';
import { useEffect } from 'react';

// Lazy-loaded heavy admin modules (code-split for faster initial load)

import './App.css';

function AppMeta() {
  const mode = getAppMode();
  const location = useLocation();

  useEffect(() => {
    // Ensure admin/private portal pages and OS domains NEVER get indexed by search engines
    if (typeof document === 'undefined') return;

    const isAdminOrPrivatePath =
      location.pathname.startsWith('/admin') ||
      location.pathname === '/login' ||
      location.pathname.startsWith('/client/confirm-staff') ||
      location.pathname.startsWith('/duty') ||
      location.pathname.startsWith('/id-card');

    const shouldNoIndex = mode === 'os' || isAdminOrPrivatePath;

    // Set page title accordingly
    if (mode === 'os' || location.pathname.startsWith('/admin')) {
      document.title = '99 Care OS — Private Portal';
    } else if (location.pathname === '/login') {
      document.title = 'Staff Login | 99 Care';
    }

    let meta = document.querySelector('meta[name="robots"]');
    if (shouldNoIndex) {
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'robots');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', 'noindex,nofollow,noarchive');
    } else if (meta) {
      meta.remove();
    }
  }, [mode, location.pathname]);

  return null;
}

import { useAttendanceSocket } from './hooks/useAttendanceSocket';

function AppContent() {
  useAttendanceSocket();
  const location = useLocation();
  const mode = getAppMode();

  if (mode === 'os') {
    return (
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          {/* In OS/Admin mode (admin.99care.org), root redirects directly to /admin */}
          <Route path="/" element={<Navigate to="/admin" replace />} />

          {/* Shared / utility routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/client/confirm-staff/:id" element={<ClientConfirmation />} />
          <Route path="/duty/:id" element={<DutyTracker />} />
          <Route path="/id-card/:token" element={<PublicIDCard />} />

          {/* Admin / CRM routes (protected) */}
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="crm" element={<ProtectedRoute requiredModule="crm"><CRM /></ProtectedRoute>} />
            <Route path="clients" element={<ProtectedRoute requiredModule="clients"><Clients /></ProtectedRoute>} />
            <Route path="hr" element={<ProtectedRoute requiredModule="hr"><HR /></ProtectedRoute>} />
            <Route path="billing" element={<ProtectedRoute requiredModule="finance"><Billing /></ProtectedRoute>} />
            <Route path="settings" element={<ProtectedRoute adminOnly><AccessControl /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Route>

          {/* Public pages fallback in OS mode */}
          <Route element={<Layout />}>
            <Route path="/about" element={<AboutPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:slug" element={<ServiceDetailPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/appointment" element={<AppointmentPage />} />
            <Route path="/appointment/confirmed" element={<AppointmentConfirmedPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Fallback to admin */}
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </AnimatePresence>
    );
  }

  // Public Mode (www.99care.org / 99care.org)
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/appointment" element={<AppointmentPage />} />
          <Route path="/appointment/confirmed" element={<AppointmentConfirmedPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Shared / utility routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/client/confirm-staff/:id" element={<ClientConfirmation />} />
        <Route path="/duty/:id" element={<DutyTracker />} />
        <Route path="/id-card/:token" element={<PublicIDCard />} />

        {/* Admin / CRM routes (accessible via www.99care.org/admin as well) */}
        <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="crm" element={<ProtectedRoute requiredModule="crm"><CRM /></ProtectedRoute>} />
          <Route path="clients" element={<ProtectedRoute requiredModule="clients"><Clients /></ProtectedRoute>} />
          <Route path="hr" element={<ProtectedRoute requiredModule="hr"><HR /></ProtectedRoute>} />
          <Route path="billing" element={<ProtectedRoute requiredModule="finance"><Billing /></ProtectedRoute>} />
          <Route path="settings" element={<ProtectedRoute adminOnly><AccessControl /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );

  // Default block (local development without VITE_APP_DOMAIN set and APP_MODE unset)
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/appointment" element={<AppointmentPage />} />
          <Route path="/appointment/confirmed" element={<AppointmentConfirmedPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        <Route path="/login" element={<Login />} />
        <Route path="/client/confirm-staff/:id" element={<ClientConfirmation />} />
        <Route path="/duty/:id" element={<DutyTracker />} />
        <Route path="/id-card/:token" element={<PublicIDCard />} />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="crm" element={<ProtectedRoute requiredModule="crm"><CRM /></ProtectedRoute>} />
          <Route path="clients" element={<ProtectedRoute requiredModule="clients"><Clients /></ProtectedRoute>} />
          <Route path="hr" element={<ProtectedRoute requiredModule="hr"><HR /></ProtectedRoute>} />
          <Route path="billing" element={<ProtectedRoute requiredModule="finance"><Billing /></ProtectedRoute>} />
          <Route path="settings" element={<ProtectedRoute adminOnly><AccessControl /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

import { AnalyticsProvider } from './components/AnalyticsProvider';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AnalyticsProvider>
          <AuthProvider>
            <ScrollToTop />
            <AppMeta />
            <Toaster position="bottom-right" theme="light" closeButton />
            <AppContent />
          </AuthProvider>
        </AnalyticsProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
